import React, { FC, useCallback } from 'react';
import classnames from 'classnames';
import { useSearchParams } from 'react-router-dom';

import DelayedAccessAlert, {
  Location
} from 'buyside/components/presentation/AccessControl/delayedAccessAlert';
import { MobileHome, HomeAside } from 'buyside/components/presentation/ActivityFeedPage/homePage';
import IdeaIndexList from 'buyside/components/presentation/IdeaIndexPage/ideaIndexList';
import IdeaIndexPageFilter, {
  HomeSavedSearchButton,
  renderIdeaCheckboxes,
  renderIdeaSideButtons,
  renderIdeaSortOrders
} from 'buyside/components/presentation/IdeaIndexPage/IdeaIndexPageFilter/ideaIndexPageFilter';
import IndexPageHeader from 'buyside/components/presentation/IndexPageHeader/indexPageHeader';

import { useIsMobile, BREAKPOINTS } from 'buyside/util/hooks/useIsMobile';
import { validatePageParam } from 'buyside/util/numbers';
import { useIdeaIndexCurrentUserQuery, useIdeaIndexListQuery } from 'interfaces/graphql-typings';

import styles from 'buyside/components/presentation/IdeaIndexPage/ideaIndexPageStyles.scss';

const IdeaIndexPage: FC<{
  isPublic: boolean;
  contestId?: string;
  hideAccessBanner?: boolean;
  isHome?: boolean;
}> = ({ isPublic, contestId, hideAccessBanner, isHome = false }) => {
  const [searchParams] = useSearchParams();
  const page = validatePageParam(searchParams);
  if (contestId) {
    searchParams.set('contest_id', contestId);
    searchParams.set('contest_ideas_only', 'true');
  }

  const { data, loading: ideasLoading, error, previousData } = useIdeaIndexListQuery({
    variables: { searchParams: searchParams.toString() }
  });
  if (error) throw error;

  const { data: userData, loading: userLoading, error: userError } = useIdeaIndexCurrentUserQuery({
    skip: isPublic
  });
  if (userError) throw userError;

  const loading = userLoading || ideasLoading;
  const edgesLength = data?.ideasIndexList?.edges?.length || 0;
  const resultCount = (data || previousData)?.ideasIndexListResultCount || 0;

  const isMobileHome = useIsMobile(BREAKPOINTS.SMALLISH) && isHome;

  const queryText = searchParams.get('text');
  const isExact = searchParams.get('exact_phrase_match');

  const renderCheckboxes = useCallback(() => renderIdeaCheckboxes(isPublic), [isPublic]);
  const renderFilters = useCallback(
    () => (
      <IdeaIndexPageFilter
        allowPremiumFilters={Boolean(userData?.currentUser?.canViewIdeaFilterDetails)}
        isHome={isHome}
      />
    ),
    [userData, isHome]
  );
  const renderSideButtons = useCallback(
    () => renderIdeaSideButtons(isPublic, Boolean(userData?.currentUser?.canWriteIdeas)),
    [userData, isPublic]
  );
  const renderSortOrders = useCallback(() => renderIdeaSortOrders(), []);
  const renderFiltersForMobileHome = useCallback(
    () => (
      <IndexPageHeader
        header={null}
        isDarkTheme
        renderCheckboxes={renderCheckboxes}
        renderFilters={renderFilters}
        renderSortOrders={renderSortOrders}
        showMoreLess
        additionalStickyElement={<HomeSavedSearchButton />}
      />
    ),
    [renderCheckboxes, renderFilters, renderSortOrders]
  );

  return (
    <main className={styles.main}>
      {hideAccessBanner ? null : (
        <DelayedAccessAlert
          id={Location.ideasIndex}
          isPublic={isPublic}
          customStyles={styles.accessAlert}
        />
      )}
      <div className={styles.indexPageContainer}>
        <div className={styles.mainIndexPageArea} id="ideaIndexPageHeader">
          {isMobileHome ? (
            <MobileHome renderFilters={renderFiltersForMobileHome}>
              <IdeaIndexList
                data={data || previousData}
                edgesLength={edgesLength}
                isExact={isExact}
                isHome={isHome}
                isPublic={isPublic}
                loading={loading}
                page={page}
                queryText={queryText}
                resultCount={resultCount}
                userData={userData}
              />
            </MobileHome>
          ) : (
            <>
              <IndexPageHeader
                header={isHome ? null : 'Ideas'}
                isDarkTheme={isHome}
                renderCheckboxes={renderCheckboxes}
                renderFilters={renderFilters}
                renderSideButtons={renderSideButtons}
                renderSortOrders={renderSortOrders}
                resultCount={resultCount}
                showMoreLess
                additionalStickyElement={isHome ? <HomeSavedSearchButton /> : null}
              />
              <div className={styles.ideaListArea}>
                <div className={classnames({ [styles.ideaList]: isHome })}>
                  <IdeaIndexList
                    data={data || previousData}
                    edgesLength={edgesLength}
                    isExact={isExact}
                    isHome={isHome}
                    isPublic={isPublic}
                    loading={loading}
                    page={page}
                    queryText={queryText}
                    resultCount={resultCount}
                    userData={userData}
                  />
                </div>
                {isHome && <HomeAside />}
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
};

export default IdeaIndexPage;
